import { useEffect } from "react";
import { Alert, Button, Form, Offcanvas } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { toast } from "react-toastify";
import FormInput from "../../../components/forms/FormInput";
import { useUpsertBdapMutation } from "../../../features/bdap/bdapApi";
import { Bdap } from "../../../features/bdap/bdapTypes";
import {
  BDAP_AMOUNTS,
  formatBdapDollars,
  rechargeLimitsForAmount,
} from "../../../features/bdap/bdapOptions";

const schema = Yup.object({
  lo_code: Yup.string().trim().required("LO code is required"),
  amount: Yup.number()
    .typeError("Select an amount")
    .oneOf([...BDAP_AMOUNTS], "Select an amount")
    .required("Select an amount"),
  recharge_limit: Yup.number()
    .typeError("Select a recharge minimum")
    .required("Select a recharge minimum"),
  accepted: Yup.boolean().oneOf([true], "Please accept and understand to continue"),
});

type FormData = Yup.InferType<typeof schema>;

interface BdapFormCanvasProps {
  show: boolean;
  onHide: () => void;
  bdap?: Bdap | null;
}

const BdapFormCanvas = ({ show, onHide, bdap }: BdapFormCanvasProps) => {
  const [upsertBdap, { isLoading }] = useUpsertBdapMutation();
  const isEdit = Boolean(bdap?.bdap_id);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      lo_code: "",
      amount: 500,
      recharge_limit: 250,
      accepted: false,
    },
  });

  const amount = Number(watch("amount"));
  const rechargeLimit = Number(watch("recharge_limit"));
  const rechargeOptions = rechargeLimitsForAmount(amount);

  useEffect(() => {
    if (!show) return;
    reset({
      lo_code: bdap?.lo_code || "",
      amount: bdap?.amount || 500,
      recharge_limit: bdap?.recharge_limit || 250,
      accepted: false,
    });
  }, [show, bdap, reset]);

  useEffect(() => {
    if (!rechargeOptions.length) return;
    if (!rechargeOptions.includes(rechargeLimit)) {
      setValue("recharge_limit", rechargeOptions[0], { shouldValidate: true });
    }
  }, [rechargeOptions, rechargeLimit, setValue]);

  const onSubmit = async (data: FormData) => {
    try {
      await upsertBdap({
        lo_code: data.lo_code.trim(),
        amount: Number(data.amount),
        recharge_limit: Number(data.recharge_limit),
        accepted: true,
      }).unwrap();
      toast.success(
        isEdit ? "BDAP details updated." : "BDAP request submitted."
      );
      onHide();
    } catch {
      toast.error("Could not save BDAP details. Please try again.");
    }
  };

  return (
    <Offcanvas
      show={show}
      onHide={onHide}
      placement="end"
      style={{ width: "560px" }}
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title className="settings-header px-2 mb-0">
          BDAP Settings
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Alert
            variant="warning"
            style={{ fontSize: "13px", lineHeight: 1.5 }}
          >
            Please note: All the requests are sent to Guild on the 10th of every
            month. You should expect to get the credits added to Memorique
            within 15 days after that.
          </Alert>

          <FormInput
            register={register}
            label="What is your LO code"
            name="lo_code"
            placeholder="Enter your LO code"
            errors={errors}
          />

          <div className="mt-4 mb-1 fw-semibold" style={{ fontSize: "14px" }}>
            Purchase Gift Credits (Funded by BDAP)
          </div>
          <p className="text-muted mb-3" style={{ fontSize: "13px" }}>
            That will be processed immediately after submitting this form to
            Guild, charged to BDAP (if funds are available), or processed by
            Guild to charge against the user's payroll by Guild (if BDAP funds
            are unavailable).
          </p>

          <Form.Group className="mb-3">
            <Form.Label>Select an amount to fund Memorique</Form.Label>
            <Form.Select {...register("amount", { valueAsNumber: true })}>
              {BDAP_AMOUNTS.map((value) => (
                <option key={value} value={value}>
                  {formatBdapDollars(value)}
                </option>
              ))}
            </Form.Select>
            {errors.amount?.message && (
              <div className="invalid-feedback d-block">
                {String(errors.amount.message)}
              </div>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Set/change a recharge minimum limit to have Guild automatically
              fund your account through a BDAP or Payroll charge
            </Form.Label>
            <Form.Select {...register("recharge_limit", { valueAsNumber: true })}>
              {rechargeOptions.map((value) => (
                <option key={value} value={value}>
                  {formatBdapDollars(value)}
                </option>
              ))}
            </Form.Select>
            {errors.recharge_limit?.message && (
              <div className="invalid-feedback d-block">
                {String(errors.recharge_limit.message)}
              </div>
            )}
          </Form.Group>

          <Alert
            variant="warning"
            style={{ fontSize: "13px", lineHeight: 1.5 }}
          >
            Please note: recharges can take up to 45 days to reflect in your
            account. If your account goes to 0 before that, we will charge your
            credit card on file or pause the shipping for the pending gifts if
            we cannot charge the card on file.
          </Alert>

          <div className="fw-semibold mb-2" style={{ fontSize: "14px" }}>
            Other important instructions:
          </div>
          <ol className="ps-3 mb-4" style={{ fontSize: "13px", color: "#495057" }}>
            <li className="mb-2">
              As soon as the credits are added to your Memorique account, you
              will receive an email about it as confirmation.
            </li>
            <li>
              If the credits go to zero before the account is recharged, the
              LO's credit card on file is charged.{" "}
              <strong>
                All accounts will require a credit card to be on file as a
                backup payment method.
              </strong>
            </li>
          </ol>

          <Form.Check
            type="checkbox"
            id="bdap-accepted"
            label="I accept and understand"
            {...register("accepted")}
            isInvalid={Boolean(errors.accepted)}
          />
          {errors.accepted?.message && (
            <div className="invalid-feedback d-block">
              {String(errors.accepted.message)}
            </div>
          )}

          <div className="d-flex justify-content-end mt-4">
            <Button type="submit" disabled={isLoading}>
              {isLoading
                ? "Saving..."
                : isEdit
                  ? "Update my request"
                  : "Submit my request"}
            </Button>
          </div>
        </form>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default BdapFormCanvas;
